import React from "react";

/**
 * Content-area skeleton shown while a lazy route chunk (or its first data) loads.
 *
 * Replaces the old full-screen brand splash (<AppLoading />) as the Suspense
 * fallback inside MainLayout — the sidebar, topbar and breadcrumb stay put and
 * only the page body swaps to a skeleton, so navigation feels like a smooth
 * content transition instead of a jarring spinner / splash flash.
 *
 * Uses zinc (dark-mode-safe) and a short fade so it never strobes on fast loads.
 */

function Bar({
  h = 16,
  w = "100%",
  className = "",
}: {
  h?: number;
  w?: number | string;
  className?: string;
}) {
  return (
    <div
      className={`bg-zinc-200 rounded ${className}`}
      style={{ height: h, width: w }}
    />
  );
}

export default function RouteSkeleton() {
  return (
    <div
      className="p-4 sm:p-6"
      style={{ animation: "v-skel-in 220ms ease-out both" }}
      aria-busy="true"
      aria-label="Loading page"
    >
      <style>{`
        @keyframes v-skel-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes v-skel-pulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
        .v-skel { animation: v-skel-pulse 1.4s ease-in-out infinite; }
      `}</style>

      <div className="v-skel">
        {/* Header row — title + primary action */}
        <div className="flex items-center justify-between mb-6">
          <Bar h={24} w={220} className="rounded-lg" />
          <Bar h={36} w={120} className="rounded-lg" />
        </div>

        {/* Primary content card */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-5">
          <Bar h={14} w="40%" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Bar h={12} w={140} />
              <Bar h={40} w="100%" className="rounded-lg" />
            </div>
          ))}
        </div>

        {/* Secondary card */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 mt-5 space-y-3">
          <Bar h={14} w="30%" />
          <Bar h={12} w="100%" />
          <Bar h={12} w="85%" />
          <Bar h={12} w="60%" />
        </div>
      </div>
    </div>
  );
}
